import { useState, useEffect, useContext, useMemo } from "react";
import { Box, TextField, Button } from "@mui/material";
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { LanguageContext } from "../context/LanguageContext";
import { statusColors } from "../constants/statusColors";

const TicketActivityHistory = ({
  newComment,
  setNewComment,
  currentUser,
  ticket,
  role,
  selectedProject,
}) => {
  const [activities, setActivities] = useState([]);
  const { translate } = useContext(LanguageContext);

  const visibleActivities = useMemo(() => {
    if (role === "Admin") return activities;

    return activities.filter(
      (a) =>
        // statu t →…
        a.action.startsWith("Statut") ||
        // création de ticket
        /Ticket créé|Ticket Created/.test(a.action) ||
        // commentaires
        a.action === "Admin Comment" ||
        a.action === "User Comment" ||
        // **upload de pièces‑jointes**
        a.action.startsWith("Pièces jointes")
    );
  }, [activities, role]);

  useEffect(() => {
    if (ticket && selectedProject) {
      const q = query(
        collection(
          db,
          "projects",
          selectedProject,
          "tickets",
          ticket.id,
          "activity"
        )
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedActivities = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Optionnel : triez par date décroissante
        fetchedActivities.sort(
          (a, b) => b.timestamp.seconds - a.timestamp.seconds
        );
        setActivities(fetchedActivities);
      });
      return () => unsubscribe();
    }
  }, [ticket, selectedProject]);

  const statusKeyMap = {
    Open: "Open",
    "Working on It": "WorkingOnIt",
    "In Staging": "InStaging",
    "In Test": "InTest",
    "In Production": "InProduction",
    "In review": "InReview",
    Closed: "Closed",
  };

  useEffect(() => {
    if (!ticket) return;
    const actCol = collection(
      db,
      "projects",
      selectedProject,
      "tickets",
      ticket.id,
      "activity"
    );
    const unsub = onSnapshot(
      query(actCol, orderBy("timestamp", "desc")),
      (snap) => setActivities(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [ticket, selectedProject]);

  const getActionIcon = (action) => {
    if (action.startsWith("Statut")) {
      const parts = action.split("→");
      const status = parts[parts.length - 1].trim();
      if (status === "Open") return "✅";
      if (status === "Working on It") return "🔧";
      if (status === "In Staging") return "🚧";
      if (status === "In Test") return "🧪";
      if (status === "In Production") return "🚀";
      if (status === "In review") return "🔍";
      if (status === "Closed") return "🛑";
    }
    if (action.match(/Ticket créé|Ticket Created/)) return "📝";
    if (action === "Admin Comment") return "💬";
    if (action.includes("Priorité")) return "⚠️";
    if (action.includes("Type")) return "🏷️";
    if (action.includes("Élément concerné") || action.includes("Description"))
      return "📄";

    return "ℹ️";
  };

  const getActionStyle = (action) => {
    if (action.startsWith("Statut")) {
      // on isole le nouveau statut (dernier segment après la flèche)
      const parts = action.split("→");
      const newStatus = parts[parts.length - 1].trim();
      const bg = statusColors[newStatus] || "#343a40";
      return { text: "#fff", background: bg };
    }
    // Création
    if (action.match(/Ticket créé|Ticket Created/))
      return { text: "#ffffff", background: "#17a2b8" }; // cyan
    // Commentaire admin
    if (action === "Admin Comment")
      return { text: "#ffffff", background: "#007bff" }; // bleu
    // Modifs spécifiques
    if (action.includes("Priorité"))
      return { text: "#ffffff", background: "#fd7e14" }; // orange vif
    if (action.includes("Type"))
      return { text: "#ffffff", background: "#6610f2" }; // violet vif
    if (action.includes("Élément concerné") || action.includes("Description"))
      return { text: "#ffffff", background: "#20c997" }; // turquoise vif

    // fallback gris foncé
    return { text: "#ffffff", background: "#343a40" };
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "10px",
          paddingBottom: "10px",
        }}
      >
        {translate("TicketActivity")}
      </div>

      <div
        style={{
          position: "relative",
          paddingLeft: "0px",
          maxHeight: "50vh",
          overflowY: "auto",
          paddingRight: "10px", //
        }}
      >
        {visibleActivities.length > 0 ? (
          <div style={{ marginBottom: "20px" }}>
            {/* {activities.map((activity) => ( */}
            {visibleActivities.map((activity) => (
              <div
                key={activity.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "15px",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  backgroundColor: "background.paper", // ✅ compatible dark/light mode
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    marginRight: "10px",
                  }}
                >
                  {/* {activity.action === "Ticket Created" ? "📝" : "💬"} */}
                  {getActionIcon(activity.action)}
                </div>
                <div>
                  <div style={{ fontWeight: "600", marginBottom: "3px" }}>
                    {activity.performedBy}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "gray",
                      marginBottom: "5px",
                    }}
                  >
                    {new Date(
                      activity.timestamp.seconds * 1000
                    ).toLocaleString()}
                  </div>

                  {activity.action &&
                    (() => {
                      const { background: chipBg } = getActionStyle(
                        activity.action
                      );

                      let label = "";

                      if (activity.action.startsWith("Statut")) {
                        // on capture l'ancien ET le nouveau statut en entier
                        const match = activity.action.match(
                          /Statut\s*:\s*(.?)\s*→\s*(.)/
                        );
                        if (match) {
                          const [, rawOld, rawNew] = match;
                          const oldKey =
                            statusKeyMap[rawOld.trim()] || rawOld.trim();
                          const newKey =
                            statusKeyMap[rawNew.trim()] || rawNew.trim();
                          label = `${translate("Status")} : ${translate(
                            oldKey
                          )} → ${translate(newKey)}`;
                        } else {
                          // fallback : on post-traite quand même
                          const parts = activity.action
                            .split("→")
                            .map((s) => s.trim());
                          const newKey =
                            statusKeyMap[parts.pop()] || parts.pop();
                          label = `${translate("Status")} → ${translate(
                            newKey
                          )}`;
                        }

                        // TYPE : A → B
                      } else {
                        const typeMatch = activity.action.match(
                          /Type\s*[:]\s*([^→]+)\s*→\s*(.+)/
                        );
                        if (typeMatch) {
                          const [, oldV, newV] = typeMatch;
                          label = `${translate("Type")} : ${translate(
                            oldV.trim()
                          )} → ${translate(newV.trim())}`;

                          // PRIORITÉ : A → B
                        } else {
                          const prioMatch = activity.action.match(
                            /Priorité\s*[:]\s*([^→]+)\s*→\s*(.+)/
                          );
                          if (prioMatch) {
                            const [, oldP, newP] = prioMatch;
                            label = `${translate("Priority")} : ${translate(
                              oldP.trim()
                            )} → ${translate(newP.trim())}`;

                            // TICKET CRÉÉ
                          } else if (
                            /Ticket créé|Ticket Created/.test(activity.action)
                          ) {
                            label = translate("TicketCreated");

                            // COMMENTAIRE ADMIN
                          } else if (activity.action === "Admin Comment") {
                            label = translate("AdminComment");

                            // fallback
                          } else {
                            label = activity.action;
                          }
                        }
                      }

                      return (
                        <div
                          style={{
                            marginBottom: activity.comment ? "5px" : "0",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              backgroundColor: chipBg,
                              color: "#ffffff",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "14px",
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })()}

                  {activity.comment && (
                    <div style={{ fontSize: "14px", color: "#333" }}>
                      {activity.comment}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontStyle: "italic", color: "#888" }}>
            {translate("NoActivityYet")}
          </div>
        )}
      </div>

      <div style={{ height: "10vh" }}>
        {role !== "Admin" && (
          <Box mt={2}>
            <TextField
              fullWidth
              variant="outlined"
              label={translate("AddComment")}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.paper", // ✅ fond cohérent avec le thème
                  color: "text.primary", // ✅ couleur du texte adaptée
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: "divider" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
                "& .MuiInputBase-input": {
                  color: "text.primary", // ✅ force la couleur du texte
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "inherit",
                  opacity: 1,
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                // if (newComment.trim() !== "") {
                if (newComment.trim()) {
                  await addDoc(
                    collection(
                      db,
                      "projects",
                      selectedProject,
                      "tickets",
                      ticket.id,
                      "activity"
                    ),
                    {
                      comment: newComment,
                      performedBy: currentUser.displayName,
                      timestamp: new Date(),
                      // action: "Admin Comment",
                      action: "User Comment",
                    }
                  );
                  setNewComment("");
                }
              }}
              style={{ marginTop: "10px" }}
            >
              {translate("PostComment")}
            </Button>
          </Box>
        )}
        {/* Admin continue de pouvoir commenter ici exactement comme avant */}
        {role === "Admin" && (
          <Box mt={2}>
            <TextField
              fullWidth
              variant="outlined"
              label={translate("AddComment")}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.paper", // ✅ fond cohérent avec le thème
                  color: "text.primary", // ✅ couleur du texte adaptée
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: "divider" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
                "& .MuiInputBase-input": {
                  color: "text.primary", // ✅ force la couleur du texte
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "inherit",
                  opacity: 1,
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                if (newComment.trim()) {
                  await addDoc(
                    collection(
                      db,
                      "projects",
                      selectedProject,
                      "tickets",
                      ticket.id,
                      "activity"
                    ),
                    {
                      comment: newComment,
                      performedBy: currentUser.displayName,
                      timestamp: new Date(),
                      action: "Admin Comment",
                    }
                  );
                  setNewComment("");
                }
              }}
              style={{ marginTop: "10px" }}
            >
              {translate("PostComment")}
            </Button>
          </Box>
        )}
      </div>
    </div>
  );
};

export default TicketActivityHistory;
